import React, { useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";
import logo from "../img/logo.png";
import backgroundImage from "../img/background1.png";

const SignIn = ({ onSignIn }) => {
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPinModal, setShowForgotPinModal] = useState(false);
  const [staffName, setStaffName] = useState("");
  const [role, setRole] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmNewPin, setConfirmNewPin] = useState("");
  const [forgotPinError, setForgotPinError] = useState("");

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-70";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      const result = await adminApiRequest({
        script: "signin",
        data: { username, pin },
      });

      if (result.success) {
        const userRole = result.admin?.role || result.role || "admin";
        localStorage.setItem("admin_token", result.token);
        localStorage.setItem("admin_expiresAt", result.expiresAt);
        localStorage.setItem("admin_username", username);
        localStorage.setItem("admin_role", userRole);

        sessionStorage.setItem("admin_activeTab", "orders");

        onSignIn(username, userRole);
      } else {
        setError(result.error || "Invalid username or PIN. Please try again.");
        setTimeout(() => setError(""), 3000);
      }
    } catch (err) {
      if (err.message === "UNAUTHORIZED") {
        setError("Invalid username or PIN.");
      } else {
        setError("An error occurred. Please try again.");
      }

      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPinSubmit = async (e) => {
    e.preventDefault();
    setForgotPinError("");

    if (newPin !== confirmNewPin) {
      setForgotPinError("PINs do not match");
      return;
    }

    setForgotPinError("Reset PIN is temporarily disabled for security.");
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat lg:bg-none"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="min-h-screen bg-slate-900/70 lg:bg-slate-100">
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
          <div
            className="relative hidden bg-cover bg-center lg:block"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          >
            <div className="absolute inset-0 bg-slate-700/85"></div>

            <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
              <div className="flex items-center gap-4">
                <img src={logo} alt="logo" className="h-14 w-14 rounded-xl" />
                <div>
                  <p className="text-sm uppercase tracking-[0.22em] text-slate-300">
                    Dee Delicious
                  </p>
                  <h1 className="text-3xl font-semibold">Admin Panel</h1>
                </div>
              </div>

              <div className="max-w-xl">
                <h2 className="text-4xl font-semibold leading-tight">
                  Manage restaurant operations from one dashboard.
                </h2>

                <p className="mt-5 leading-relaxed text-slate-300">
                  Sign in to access the admin panel and continue managing menu
                  items, orders and inventory.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center px-4 py-10 sm:px-6 lg:px-10">
            <div className="w-full max-w-md">
              <div className="mb-8 flex flex-col items-center text-center text-white lg:hidden">
                <img src={logo} alt="logo" className="h-16 w-16 rounded-2xl" />
                <p className="mt-4 text-xs uppercase tracking-[0.22em] text-slate-300">
                  Dee Delicious
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-white">
                  Admin Panel
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-200">
                  Sign in to access the admin panel and continue managing menu
                  items, orders and inventory.
                </p>
              </div>

              <div className="rounded-3xl border border-white/20 bg-white/95 p-6 shadow-xl backdrop-blur sm:p-8 lg:border-slate-200 lg:bg-white">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-slate-900">
                    Sign In
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Enter your username and PIN to continue.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Username
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter Username"
                      className={inputClass}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      PIN
                    </label>
                    <input
                      type="password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      placeholder="Enter PIN"
                      className={inputClass}
                      required
                      minLength={6}
                      maxLength={6}
                      disabled={loading}
                    />
                  </div>

                  {error && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {loading ? (
                      <span className="flex items-center gap-3">
                        <span className="h-5 w-5 rounded-full border-2 border-white/80 border-t-transparent animate-spin"></span>
                        Signing in...
                      </span>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {showForgotPinModal && (
          <Modal
            onClose={() => setShowForgotPinModal(false)}
            title="Reset Your PIN"
            onSubmit={handleForgotPinSubmit}
            submitButtonText="Reset"
            showCancel={true}
            showSubmitButton={true}
          >
            <div className="space-y-4">
              <input
                type="text"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                placeholder="Enter Staff Name"
                className={inputClass}
                required
              />

              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Enter Role"
                className={inputClass}
                required
              />

              <input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                placeholder="Enter New PIN"
                className={inputClass}
                required
                minLength={6}
                maxLength={6}
              />

              <input
                type="password"
                value={confirmNewPin}
                onChange={(e) => setConfirmNewPin(e.target.value)}
                placeholder="Confirm New PIN"
                className={inputClass}
                required
                minLength={6}
                maxLength={6}
              />

              {forgotPinError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600">
                  {forgotPinError}
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default SignIn;